import {downloadCsvFromCdnUrl, downloadRowCsv, getDateWithTimeString} from '@/helpers/shadcn/utils'
import {useDataTable} from '@/hooks/useDataTable-hook'
import {GetFormattedInventory} from '@/models/InventoryModel'
import {InventoryListColConfig} from '@/services/MockData/InventoryList/InventorytColConfig'
import {
  getInventory,
  getInventoryCsvFile,
  getInventoryFileUploadErrors,
} from '@/services/ProductsService/Fulfillment/inventory'
import {DEFAULT_TABLE_PAGE_SIZE} from '@/utils/constants/commonConstants'
import {useQueries} from '@tanstack/react-query'
import {useMemo} from 'react'

export const useInventory = ({isSelectAll}) => {
  const {
    setSortingObj,
    setSearchArry,
    pageIndex,
    currentPage,
    sortingObj,
    searchArr,
    onPageIndexChange,
    createSearchArray,
    resetAllBtnClicked,
  } = useDataTable()

  const queryParams = useMemo(() => {
    const params = new URLSearchParams()
    if (sortingObj?.column) {
      params.append('sortBy', sortingObj?.column)
      params.append('order', sortingObj?.direction)
    } else {
      params.append('sortBy', 'productId')
      params.append('order', 'DESC')
    }
    params.append('page', pageIndex)
    params.append('pageSize', DEFAULT_TABLE_PAGE_SIZE)

    return params.toString()
  }, [sortingObj?.column, sortingObj?.direction, pageIndex])

  const [inventoryResult] = useQueries({
    queries: [
      {
        queryKey: ['INVENTORY', queryParams, searchArr],
        queryFn: () => getInventory(searchArr, queryParams),
      },
    ],
  })

  const onDownloadBtnClick = async selectedData => {
    try {
      // Validate input
      if (!selectedData || !Array.isArray(selectedData) || selectedData.length === 0) {
        throw new Error('No data selected for download')
      }

      const selectedBody = selectedData.map(item => ({
        productId: item.productId || '',
        warehouseId: item.warehouseId || '',
      }))

      // Filter out items without productId
      const validBody = selectedBody.filter(item => item?.productId)
      if (validBody.length === 0) {
        throw new Error('No valid product IDs in selection')
      }
      const defaultSortingObj =
        Object.keys(queryParams)?.length > 0
          ? `&${queryParams}`
          : `sortBy=productId&order=DESC&page=${currentPage}&pageSize=10`
      const body = {
        ids: isSelectAll ? [] : validBody,
        filters: searchArr,
      }
      
      const data = await getInventoryCsvFile(body, defaultSortingObj)

      // Check for new response structure with cdnUrl
      if (data?.cdnUrl) {
        const fileName = `Inventory_${getDateWithTimeString()}.csv`
        downloadCsvFromCdnUrl(data.cdnUrl, fileName)
      }
      // Fallback to old response structure with blobUrl and sasToken
      else if (data?.blobUrl && data?.sasToken) {
        const fileName = `Inventory_${getDateWithTimeString()}`
        downloadRowCsv(data.blobUrl, data.sasToken, fileName)
      } else {
        throw new Error('Invalid response from server')
      }
    } catch (error) {
      console.error('Download error:', error)
      throw error // Re-throw to be handled by the caller
    }
  }

  const resultsData = useMemo(() => inventoryResult?.data || {}, [inventoryResult?.data])
  const inventoryData = useMemo(() => GetFormattedInventory(resultsData), [resultsData])

  const fetchUploadErrors = async parentId => {
    const res = await getInventoryFileUploadErrors(parentId)

    const productRows = res?.rows || res?.data || []

    const flattened = productRows.map(row => {
      const pid = row?.productId ?? '-'
      const wid = row?.locationId ?? row?.warehouseId ?? '-'
      const errorsArr = Array.isArray(row?.errors) ? row.errors : []

      // If there are no error details, return placeholders
      if (errorsArr.length === 0) {
        return {
          productId: pid,
          warehouseId: wid,
          errorCol: '-',
          errorMsg: '-',
        }
      }

      // Build a combined error message string: "<errorCol> <errorMsg>" joined by comma
      const combinedErrorMsg = errorsArr
        .map(errItem => {
          const errorCol = errItem?.errorCol ?? '-'
          const errorMsg = errItem?.errorMsg ?? '-'
          return `${errorCol} ${errorMsg}`.trim()
        })
        .join(' , ')

      return {
        productId: pid,
        warehouseId: wid,
        errorCol: '',
        errorMsg: combinedErrorMsg,
      }
    })

    return flattened
  }

  return {
    inventoryData,
    inventoryColConfigData: InventoryListColConfig,
    setSortingObj,
    setSearchArry,
    onDownloadBtnClick,
    inventoryResult,
    onPageIndexChange,
    currentPage,
    createSearchArray,
    resetAllBtnClicked,
    fetchUploadErrors,
  }
}
